/* import __COLOCATED_TEMPLATE__ from './appointment.hbs'; */
import { action } from '@ember/object';
import { ensureSafeComponent } from '@embroider/util';
import { humanDateFormat } from 'client-portal/utils/date-time';
import { importSync } from '@embroider/macros';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import moment from 'moment-timezone';
import styles from './appointment.module.scss';

export default class SiteBillingAccountHistoryContainersAppointment extends Component {
  @service store;
  @tracked timelineVisibility = {};
  @tracked timelineData = {};
  @tracked timelineAnimationState = {};
  styles = styles;
  humanDateFormat = humanDateFormat;

  @(task(function* (row) {
    let entries = yield this.store.query('appointment-billing-timeline-entry', {
      filter: {
        appointmentId: row.id,
      },
    });
    return entries;
  }).keepLatest())
  fetchAppointmentTimeline;

  @action
  async handleTimelineToggle(isVisible, item) {
    const appointmentId = item.id;

    if (isVisible) {
      this.timelineVisibility = { ...this.timelineVisibility, [appointmentId]: true };

      try {
        const timelineData = await this.fetchAppointmentTimeline.perform(item);
        this.timelineData = { ...this.timelineData, [appointmentId]: timelineData };

        // eslint-disable-next-line ember/no-runloop
        later(() => {
          this.timelineAnimationState = {
            ...this.timelineAnimationState,
            [appointmentId]: 'fade-in',
          };
        }, 5);
      } catch (error) {
        this.timelineData = { ...this.timelineData, [appointmentId]: [] };
      }
    } else {
      this.timelineAnimationState = { ...this.timelineAnimationState, [appointmentId]: 'fade-out' };

      // eslint-disable-next-line ember/no-runloop
      later(() => {
        this.timelineVisibility = { ...this.timelineVisibility, [appointmentId]: false };
        this.timelineAnimationState = { ...this.timelineAnimationState, [appointmentId]: 'hidden' };
      }, 500);
    }
  }

  get columns() {
    const APPOINTMENTS_COLUMNS = [
      { name: '', componentSuffix: 'timeline-toggle' },
      { name: 'Date', componentSuffix: 'date' },
      { name: 'Details', componentSuffix: 'details' },
      { name: 'Charges', componentSuffix: 'charges' },
      { name: 'Payments', componentSuffix: 'payments' },
      { name: 'Balance', componentSuffix: 'balance' },
    ];

    return APPOINTMENTS_COLUMNS.map(column => {
      let module = importSync(`../cells/${column.componentSuffix}`);
      column.columnComponent = ensureSafeComponent(module.default, this);

      if (column.componentSuffix === 'timeline-toggle') {
        column.onToggle = this.handleTimelineToggle;
      }

      return column;
    });
  }

  get isTimelineVisibleForAppointment() {
    return appointmentId => {
      return this.timelineVisibility[appointmentId] || false;
    };
  }

  get getTimelineDataForAppointment() {
    return appointmentId => {
      return this.timelineData[appointmentId] || [];
    };
  }

  get getGroupedTimelineDataForAppointment() {
    return appointmentId => {
      const entries = this.timelineData[appointmentId] || [];
      const groupsMap = new Map();
      const groups = [];

      entries.forEach(entry => {
        const date = entry.entryDate;
        const key = date ? moment(date).format('YYYY-MM-DD') : 'unknown';
        let group = groupsMap.get(key);
        if (!group) {
          group = { date, entries: [] };
          groupsMap.set(key, group);
          groups.push(group);
        }
        group.entries.push(entry);
      });

      return groups;
    };
  }

  get isTimelineLoading() {
    return appointmentId =>
      this.fetchAppointmentTimeline.isRunning && !this.timelineData[appointmentId];
  }

  get getTimelineAnimationClass() {
    return appointmentId => this.timelineAnimationState[appointmentId];
  }
}
